/**
 * Step content validation utilities (Step domain)
 * Prevents blank screens by validating step data before rendering
 * Moved from shared/utilities to Step module for domain encapsulation.
 */

import type { Step } from 'src/modules/Step/models/Step';
import type { Drill } from 'src/modules/Step/models/Drill';
import type { Stack } from 'src/modules/Step/models/Stack';
import type { TheoryContent, StepTypeKebabCase } from 'src/types/course/step';
import { stackKindOf } from './stackKindOf';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validate step has all required data for rendering
 *
 * @param step - Step model from Pinia ORM
 * @param stepType - Type of step (drill, theory, filter, etc.)
 * @returns Validation result with errors and warnings
 */
export function validateStepContent(
  step: Step | null | undefined,
  stepType: StepTypeKebabCase | null,
): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Basic step validation
  if (!step) {
    errors.push('Step data is missing');
    return { isValid: false, errors, warnings };
  }

  if (!step.step_name) {
    warnings.push('Step name is missing');
  }

  if (!step.step_type || step.step_type.length === 0) {
    errors.push('Step type is missing');
    return { isValid: false, errors, warnings };
  }

  const stepTypeData = step.step_type[0];

  if (!stepTypeData) {
    errors.push('Step type data is missing');
    return { isValid: false, errors, warnings };
  }

  // Theory step validation
  if (stepType === 'theory') {
    return validateTheoryContent(stepTypeData.theory_content, errors, warnings);
  }

  // Drill-based step validation (all non-theory steps)
  if (stepType) {
    const drill = stepTypeData.drill;
    const stack = stepTypeData.stack;

    if (!drill) {
      errors.push('Drill data is missing');
    } else {
      validateDrillContent(drill, stepType, errors, warnings);
    }

    if (!stack && stepType !== 'filter') {
      errors.push('Stack data is missing');
    } else if (stack) {
      validateStackContent(stack, stepType, errors);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validate theory step content
 */
function validateTheoryContent(
  theoryContent: TheoryContent[] | null | undefined,
  errors: string[],
  warnings: string[],
): ValidationResult {
  if (!theoryContent || theoryContent.length === 0) {
    errors.push('Theory content is missing');
    return { isValid: false, errors, warnings };
  }

  theoryContent.forEach((content, index) => {
    if (!content.__component) {
      errors.push(`Theory content item ${index + 1} is missing component type`);
    }

    if (!content.title) {
      warnings.push(`Theory content item ${index + 1} is missing title`);
    }

    // Validate based on component type. Strapi sends the kebab-case UID
    // ('theory.html-content'); the snake_case spelling is kept for old data.
    // Content problems are warnings: they never block the student.
    switch ((content.__component as string | undefined)?.replace(/-/g, '_')) {
      case 'theory.html_content':
        if (!content.html) {
          warnings.push(
            `Theory HTML content item ${index + 1} is missing HTML`,
          );
        }
        break;
      case 'theory.image_content':
        if (!content.image?.url) {
          warnings.push(
            `Theory image content item ${index + 1} is missing image URL`,
          );
        }
        break;
      case 'theory.video_embed_content':
        if (!content.video_embed?.url) {
          warnings.push(
            `Theory video content item ${index + 1} is missing video URL`,
          );
        }
        break;
    }
  });

  return { isValid: errors.length === 0, errors, warnings };
}

/**
 * Validate drill content
 */
function validateDrillContent(
  drill: Drill,
  stepType: StepTypeKebabCase,
  errors: string[],
  warnings: string[],
): void {
  if (!drill.drill_name) {
    warnings.push('Drill name is missing');
  }

  if (!drill.drill_category) {
    warnings.push('Drill category is missing');
  }

  if (!drill.type) {
    warnings.push('Drill type is missing');
  } else if (stepType !== 'filter' && kebabTypeOf(drill.type) !== stepType) {
    // The view follows the step's type, so the drill's own type settings
    // (e.g. a Pairs reply method on a Single Choice step) would be ignored.
    errors.push(
      `Drill "${drill.drill_name ?? ''}" is a ${drill.type} drill, but the step is ${stepType}`,
    );
  }

  // Check for drill_type (specific drill configuration)
  if (
    !drill.drill_type ||
    (Array.isArray(drill.drill_type) && drill.drill_type.length === 0)
  ) {
    warnings.push(`Drill type configuration is missing for ${stepType} step`);
  }

  // Validate settings based on step type
  if (stepType !== 'filter' && !drill.settings) {
    warnings.push('Drill settings are missing');
  }

  if (stepType === 'filter' && !drill.filter_settings) {
    errors.push('Filter settings are missing for filter step');
  }
}

/**
 * Validate stack content
 */
function validateStackContent(
  stack: Stack,
  stepType: StepTypeKebabCase,
  errors: string[],
): void {
  if (!stack.stack_source || stack.stack_source.length === 0) {
    // The step GET only populates `stack.<step kind>` items, so a stack of
    // another type (picked by mistake in the admin) comes back empty.
    const stackKind = stack.type ? kebabTypeOf(stack.type) : '';
    const expected = stackKindOf(stepType);
    if (stackKind && stackKind !== expected) {
      const needs = expected === stepType ? '' : ` (needs a ${expected} stack)`;
      errors.push(
        `Stack "${stack.name ?? ''}" is a ${stack.type} stack, but the step is ${stepType}${needs}`,
      );
      return;
    }
    errors.push('Stack source is missing or empty');
    return;
  }

  const stackSource = stack.stack_source[0];
  if (!stackSource) {
    errors.push('Stack source data is missing');
    return;
  }

  // The items key of the stack type the step plays (true-false → vocabulary_item)
  const itemsKey = `${stackKindOf(stepType).replace(/-/g, '_')}_item`;
  const items = (stackSource as Record<string, unknown>)[itemsKey];

  if (!items || (Array.isArray(items) && items.length === 0)) {
    errors.push(`Stack items (${itemsKey}) are missing or empty`);
  }
}

/**
 * Quick validation check - returns boolean only
 *
 * @param step - Step model
 * @param stepType - Type of step
 * @returns true if step has minimum required data
 */
export function isStepContentValid(
  step: Step | null | undefined,
  stepType: StepTypeKebabCase | null,
): boolean {
  const result = validateStepContent(step, stepType);
  return result.isValid;
}

/**
 * Get human-readable validation error message
 *
 * @param result - Validation result
 * @returns Formatted error message for display
 */
export function getValidationErrorMessage(result: ValidationResult): string {
  if (result.isValid) return '';

  const errorMessages =
    result.errors.length > 0 ? `Errors: ${result.errors.join(', ')}` : '';

  const warningMessages =
    result.warnings.length > 0 ? `Warnings: ${result.warnings.join(', ')}` : '';

  return [errorMessages, warningMessages].filter(Boolean).join(' | ');
}

/** 'Single Choice' / 'SingleChoice' (a drill or stack `type`) → 'single-choice'. */
function kebabTypeOf(type: string): string {
  return type
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\s+/g, '-');
}
